/**
 * Read-only guard for admin impersonation ("Enter User Profile").
 *
 * While an admin is impersonating a user, the app renders that user's data but
 * Firebase Auth still holds the admin's session. Any write would therefore be
 * attributed to the wrong identity, and an admin acting on a customer's account
 * without leaving an audit trail is not something we want to allow. So
 * impersonation is strictly read-only: reads are scoped to the impersonated
 * user, writes are blocked.
 *
 * The flag lives at module scope rather than in React context so that services
 * (which have no access to hooks) can enforce it too. `AdminContext` keeps it in
 * sync; the initial value is read straight from sessionStorage so that writes
 * are never briefly allowed during the async admin-status check on page load.
 */

export const IMPERSONATION_STORAGE_KEY = 'adminImpersonatingUserId';

export const IMPERSONATION_READ_ONLY_MESSAGE =
  'You are viewing this account as an admin. Exit Admin Mode to make changes.';

/** Thrown when a write is attempted while impersonating. */
export class ImpersonationReadOnlyError extends Error {
  readonly code = 'impersonation/read-only';

  constructor(action?: string) {
    super(
      action
        ? `Blocked "${action}" during admin impersonation. ${IMPERSONATION_READ_ONLY_MESSAGE}`
        : IMPERSONATION_READ_ONLY_MESSAGE
    );
    this.name = 'ImpersonationReadOnlyError';
  }
}

const readStoredImpersonation = (): boolean => {
  try {
    return !!sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // sessionStorage can throw in private-mode / SSR contexts.
    return false;
  }
};

let readOnly = readStoredImpersonation();

/** Called by AdminContext whenever impersonation starts or stops. */
export const setImpersonationReadOnly = (active: boolean): void => {
  readOnly = active;
};

/** True when the current session is an admin viewing someone else's account. */
export const isImpersonationReadOnly = (): boolean => readOnly;

/**
 * Throws if a write is attempted while impersonating. Call this at the top of
 * any service method that mutates user data.
 */
export const assertNotImpersonating = (action?: string): void => {
  if (readOnly) {
    throw new ImpersonationReadOnlyError(action);
  }
};

/** True when the error came from this guard, for friendlier UI messaging. */
export const isImpersonationReadOnlyError = (error: unknown): boolean =>
  error instanceof ImpersonationReadOnlyError ||
  (typeof error === 'object' &&
    error !== null &&
    (error as { code?: string }).code === 'impersonation/read-only');
